import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { IconButton, Label } from 'shared/ui'
import { useShortcutTooltip } from 'shared/ui/ShortcutTooltip/useShortcutTooltip'
import { useChips } from 'shared/hooks/useChips.ts'
import { PromptFieldText } from 'entities/Chatbot/utils/constants'
import {
  EnhancePrompt,
  type IEnhancePromptProps,
} from 'entities/AIAgent/actions/enhancePrompt/EnhancePrompt'
import { AiCallingApi } from 'entities/AICalling/api/aiCallingApi'
import { sendEnhancePromptLogEvent } from 'entities/AIAgent/actions/enhancePrompt/sendEnhancePromptLogEvent'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type IAICallingType } from 'entities/AICalling/api/types'
import { ActionTemplateMap, type IMessageActionData } from 'entities/AIAgent/lib/agentActions.ts'
import { type IAgentHandoffNotifySettings } from 'entities/Chatbot'
import { MessageField } from 'widgets/MessageField'
import { ShortcutContent } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/ShortcutContent/ShortcutContent.tsx'
import { sendSMS } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/sendSMS/sendSMS.tsx'
import { handoffToHuman } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/handoffToHuman/handoffToHuman.tsx'
import { type AgentInstructionsStore } from './store/AgentInstructionsStore'

const agentTypeMap: Record<IAICallingType, string> = {
  faq_information: 'FAQ',
  custom: 'custom',
  take_message: 'take a message',
}

export const AgentInstructions = observer(({ store }: { store: AgentInstructionsStore }) => {
  const {
    init,
    error,
    setError,
    instructions,
    setInstruction,
    resetInstructions,
    parent,
    messageFieldStore,
    onUpdateSMSAction,
    onUpdateHandoffToHumanAction,
    getAgentAction,
    deleteAgentAction,
    deleteAction,
  } = store
  const entities = useEntitiesContext()
  const { textFieldElement, replaceTriggerContent, updateTriggerContent } = messageFieldStore

  const { triggerTooltip, infos } = useShortcutTooltip({
    textFieldElement,
    actions: [
      {
        shortcut: '/',
        infoLabel: 'to add contact fields and actions',
        renderComponent: (onClose) => <ShortcutContent store={store} onClose={onClose} />,
      },
    ],
  })

  useChips({
    textFieldElement,
    replaceTriggerContent,
    updateTriggerContent,
    actions: {
      [ActionTemplateMap.sendSMS]: (chip, data: IMessageActionData) => {
        if ('token_id' in data) {
          const token_id = data.token_id
          if (token_id) {
            const action = getAgentAction(token_id)
            if (action) {
              sendSMS({
                onSave: onUpdateSMSAction,
                action,
                onDelete: () => {
                  deleteAction(chip)
                  deleteAgentAction(token_id)
                },
              })
            }
          }
        }
      },
      [ActionTemplateMap.handoffToHuman]: (chip, data: IAgentHandoffNotifySettings) => {
        handoffToHuman({
          onSave: (data) => onUpdateHandoffToHumanAction(chip, data),
          onDelete: () => deleteAction(chip),
          data,
        })
      },
    },
  })

  useEffect(init, [])

  const requestEnhance: IEnhancePromptProps['requestEnhance'] = async (prompt, signal) => {
    if (!parent.params) return ''
    const template_type = parent.params?.template_type

    sendEnhancePromptLogEvent(entities, {
      agent_id: parent.response?.id,
      agentType: template_type ? `calling agent - ${agentTypeMap[template_type]}` : '',
    })

    const res = await AiCallingApi.enhancePrompt(
      {
        instructions: prompt,
        template_type,
        knowledge_base_enabled: parent.params.knowledge_base_enabled,
        knowledge_base_all_documents: parent.params.knowledge_base_all_documents,
        knowledge_base_documents: parent.params.knowledge_base_documents,
      },
      { signal }
    )
    return res.data.improved_prompt
  }

  return (
    <div>
      {triggerTooltip}
      <Label
        marginBottom={4}
        questionTooltipProps={{
          label: PromptFieldText.instructions.tooltipLabel,
          breakWord: true,
        }}
        label={{
          text: 'Agent instructions',
          rightNode: (
            <>
              <EnhancePrompt
                prompt={instructions}
                onAcceptPrompt={setInstruction}
                setError={setError}
                requestEnhance={requestEnhance}
              />
              <IconButton
                iconProps={{ fontSize: 16 }}
                variant={'icon'}
                color={'tertiary'}
                size={'small'}
                icon={'back'}
                ariaLabel='Reset'
                onClick={resetInstructions}
                tooltipProps={{
                  label: 'Reset to default',
                  placement: 'top',
                }}
              />
            </>
          ),
        }}
      />
      <MessageField
        messageFieldStore={messageFieldStore}
        uiStyle={'new'}
        error={error}
        additionalBottomContent={infos}
      />
    </div>
  )
})
